import { z } from 'zod';
import { IsoTimestamp } from './common';

/**
 * Cross-user form-learnings — the worker's "I tried this Greenhouse form
 * and it broke; here's what worked next time" library, persisted in the
 * cloud so every user benefits from every other user's failures.
 *
 * Keyed by `(atsDomain, signature)` so one user's discovery on a specific
 * form template flows back to anyone who hits the same form within
 * minutes. `signature` is a content-hash of the form's structural
 * skeleton — see `computeFormSignature()` in worker-core.
 */

export const FormStrategySource = z.enum(['pattern', 'llm', 'manual', 'failure']);
export type FormStrategySource = z.infer<typeof FormStrategySource>;

export const FormStrategy = z.object({
  trigger: z.string().min(1),
  action: z.string().min(1),
  confidence: z.number().min(0).max(1),
  source: FormStrategySource.default('llm'),
});
export type FormStrategy = z.infer<typeof FormStrategy>;

export const FormLearning = z.object({
  /** ATS provider — 'workday' | 'greenhouse' | 'ashby' | 'lever' | etc. */
  atsType: z.string().min(1),
  /** Hostname extracted from the form's URL. */
  atsDomain: z.string().min(1),
  /** Content-hash of the form's structural skeleton — keeps learnings
   *  scoped to the exact template, not just "any Greenhouse form". */
  signature: z.string().min(8),
  strategies: z.array(FormStrategy),
  /** Sample of failure types (`form_filling_failed`, `external_no_submit`, etc.)
   *  the learnings have already addressed — used by the worker to rank
   *  applicability. */
  failureTypes: z.array(z.string()).default([]),
  /** Number of distinct users who have contributed to this entry — drives
   *  the worker's "high-trust" rendering threshold. */
  contributorCount: z.number().int().nonnegative().default(1),
  /** Last user-id that contributed. Anonymized to a 16-char prefix on
   *  read so the response doesn't fan a private id across the user base. */
  lastContributorPrefix: z.string().optional(),
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
});
export type FormLearning = z.infer<typeof FormLearning>;

export const PublishFormLearningRequest = z.object({
  atsType: z.string().min(1),
  atsDomain: z.string().min(1),
  signature: z.string().min(8),
  strategies: z.array(FormStrategy).min(1).max(50),
  failureType: z.string().optional(),
});
export type PublishFormLearningRequest = z.infer<typeof PublishFormLearningRequest>;

export const PublishFormLearningResponse = z.object({
  atsDomain: z.string(),
  signature: z.string(),
  strategyCount: z.number().int().nonnegative(),
  contributorCount: z.number().int().nonnegative(),
  /** True when the worker's submission actually changed the stored set —
   *  false when the strategies were merged into an already-known set. */
  changed: z.boolean(),
});
export type PublishFormLearningResponse = z.infer<typeof PublishFormLearningResponse>;

export const ListFormLearningsResponse = z.object({
  items: z.array(FormLearning),
});
export type ListFormLearningsResponse = z.infer<typeof ListFormLearningsResponse>;
